//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';

import { ScheduledTweetValidationErrorType } from '@apis/twitter-scheduled-tweets/twitterScheduledTweetsApiModel';
import { UniversalScheduledPostConfigType } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import {
  MAX_TWITTER_MEDIA_HEIGHT_PX,
  MAX_TWITTER_MEDIA_WIDTH_PX,
  MIN_TWITTER_MEDIA_HEIGHT_PX,
  MIN_TWITTER_MEDIA_WIDTH_PX,
} from '@twitter/redux/post-details/twitterPostDetailsModel';

export const TWITTER_POST_PREVIEW_MEDIA_WIDTH = 389;

const TWITTER_IMAGE_ASPECT_RATIO = 16 / 9;
export const TWITTER_MAX_IMAGE_HEIGHT =
  TWITTER_POST_PREVIEW_MEDIA_WIDTH / TWITTER_IMAGE_ASPECT_RATIO;

export const TWITTER_POST_LINK_PREVIEW_IMAGE_HEIGHT = 250;

export const TWITTER_ROUNDED_BORDER_RADIUS = 10;

export const TWITTER_POST_PREVIEW_BORDER_STYLE = `${theme.borderWidths[1]} solid ${theme.colors.gray2}`;
export const TWITTER_THUMBNAIL_ERROR_BORDER_STYLE = `${theme.borderWidths[2]} solid ${theme.colors.aliases.error}`;

export const TwitterMediaSectionDisabledCtaTooltip = {
  MaxImagesAdded: `You may only post up to 4 images at a time.`,
  VideoAdded: `You may only add one video at a time. If you'd like to add photos or a GIF, remove this video.`,
  GifAdded: `You may only add one GIF at a time. If you'd like to add photos or a video, remove this GIF.`,
} as const;

export const TwitterPostDetailsFormFieldErrorText = {
  MediaTooSmallText: `The media you selected cannot be scheduled because it does not meet
  X's image size requirements. Please use an image larger than
  ${MIN_TWITTER_MEDIA_WIDTH_PX}x${MIN_TWITTER_MEDIA_HEIGHT_PX}px.`,
  MediaTooLargeText: `The media you selected cannot be scheduled because it does not meet
  X's image size requirements. Please use an image smaller than
  ${MAX_TWITTER_MEDIA_WIDTH_PX}x${MAX_TWITTER_MEDIA_HEIGHT_PX}px.`,
  NoMediaOrText: 'Please add either media or text to this post.',
  NoPostDate: 'Please select a date to schedule this post.',
  NoPostTime: 'Please select a time to schedule this post.',
  PostTimeInPast:
    'Please select a post time in the future to schedule this post.',
  TextTooLong: 'Please enter 280 characters or less.',
} as const;

export const PostDetailsSchedulingErrorMessages = {
  TwitterPostDetailsGenericError:
    'Something went wrong. Please try again or contact Bazaarvoice Support.',
  TwitterPostDetailsPostFailureMessage:
    'We were unable to post to X at the scheduled time. Please try again.',
  TwitterPostDetailsLinkPreviewDisclaimer:
    'The link preview above may vary from the published post.',
  RequestSaveDraftTweetFailed: `Failed to save draft post type: ${UniversalScheduledPostConfigType.TwitterMediaPost}`,
  RequestLoadDraftTweetFailed: `Failed to load draft post type: ${UniversalScheduledPostConfigType.TwitterMediaPost}`,
  RequestDeleteTweetFailed: `Failed to delete post`,
} as const;

export const TwitterPostDetailsScheduleErrorContextMessage: Record<
  ScheduledTweetValidationErrorType,
  string
> = {
  [ScheduledTweetValidationErrorType.MissingMediaAndText]:
    'Please add either media or text to this post and try again.',
  [ScheduledTweetValidationErrorType.GifFileSize]:
    'The GIF you selected cannot be scheduled. Please reduce the file size to less than 15 MB and try again.',
  [ScheduledTweetValidationErrorType.ImageFileSize]:
    'The image you selected cannot be scheduled. Please reduce the file size to less than 5 MB and try again.',
  [ScheduledTweetValidationErrorType.MismatchedMediaType]:
    'The gallery of media you selected cannot be scheduled because it contains video/gif content. Please remove them and try again.',
  [ScheduledTweetValidationErrorType.TooManyGifs]:
    'A maximum of 1 GIF is allowed in a post.',
  [ScheduledTweetValidationErrorType.TooManyImages]:
    'A maximum of 4 images are allowed in a post.',
  [ScheduledTweetValidationErrorType.TooManyVideos]:
    'A maximum of 1 video is allowed in a post.',
  [ScheduledTweetValidationErrorType.VideoAspectRatio]:
    'The video you selected cannot be scheduled because it does not meet Twitter’s aspect ratio requirements (between 1:3 and 3:1).',
  [ScheduledTweetValidationErrorType.VideoDuration]:
    'The video you selected cannot be scheduled. Please reduce its length to less than 140 seconds and try again.',
  [ScheduledTweetValidationErrorType.VideoFileSize]:
    'The video you selected cannot be scheduled. Please reduce the file size to less than 512 MB and try again.',
};
